import type {CSSProperties, MouseEvent, ReactElement} from 'react';
import {IconWithTooltip} from '@Components/icon-with-tooltip/icon-with-tooltip';
import {TooltipPlacement} from '@Components/icon-with-tooltip/icon-with-tooltip.types';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {updatePopUpStateAndPosition} from '@Components/poster-editor/components/poster-popup-menu/poster-popup-menu-slice';
import type {PopupAnchorRect} from '@Components/poster-editor/components/poster-popup-menu/poster-popup-menu.types';
import {POPUP_TOGGLE_MARKER_ATTR} from '@Components/poster-editor/components/poster-popup-menu/poster-popup-menu.types';
import {getGa4ItemTypeForTimelineSelection} from '@Components/poster-editor/components/timeline/utils/timeline-tracking';
import {openPosterEditorMoreOptionsModal} from '@Modals/poster-editor-more-options-modal/poster-editor-more-options-modal';
import {useSingleActivePosterItem} from '@Hooks/poster-editor/useSingleActivePosterItem';
import {useFullySelectedGroupId} from '@Hooks/poster-editor/useFullySelectedGroupId';
import {useGetSelectedSlideId} from '@Hooks/poster-editor/useGetSelectedSlideId';
import {GA4EventName, GA4EventParamName, GA4EventSources, ItemMenuOpenSource, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {trackEditorItemDeleted, trackItemMenuOpened} from '@Libraries/poster-editor-tracking.library';
import {isIconImageItem, isItemStickerImage} from '@Libraries/image-item.library';
import type {Item} from '@PosterWhiteboard/items/item/item.class';
import type {ImageItem} from '@PosterWhiteboard/items/image-item/image-item.class';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {getItemActionBarAnchor} from './custom-item-action-bar.library';
import type {ItemActionBarLeadingAction, ItemActionBarProps} from './custom-item-action-bar.types';
import {CUSTOM_ITEM_ACTION_BAR_ID, CUSTOM_REPLACE_CONTROL_ID, ItemActionBarAction} from './custom-item-action-bar.types';
import styles from './custom-item-action-bar.module.scss';

/** Floating pill under the selection: item-specific buttons (Replace, Edit, previous/next slide, Group/Ungroup), then Duplicate, Delete and More (the right-click menu). */
export function CustomItemActionBar(props: ItemActionBarProps): ReactElement | null {
  const dispatch = useAppDispatch();
  const activeItem = useSingleActivePosterItem(props.pageHashedId);
  const fullySelectedGroupId = useFullySelectedGroupId(props.pageHashedId);
  // Subscribing re-renders the bar when the shown slide changes, so the previous/next buttons enable and disable with it.
  useGetSelectedSlideId();

  const isMobileVariant = useAppSelector((state) => {
    return state.posterEditor.isMobileVariant;
  });
  const isMoreMenuOpen = useAppSelector((state) => {
    return state.posterPopUpMenu.openPosterPopUpMenu;
  });
  const isReplaceMenuOpen = useAppSelector((state) => {
    return state.imageItemPopUpMenu.openPopup;
  });
  const selectedItemCount = useAppSelector((state) => {
    return state.posterEditor.activeItemIds[props.pageHashedId]?.length ?? 0;
  });
  const isSelectionTransforming = useAppSelector((state) => {
    return (
      state.posterEditor.arePosterItemsMoving[props.pageHashedId] ||
      state.posterEditor.arePosterItemsScaling[props.pageHashedId] ||
      state.posterEditor.arePosterItemsRotating[props.pageHashedId]
    );
  });

  const page = window.posterEditor?.whiteboard?.pages.getPage(props.pageHashedId);

  if (!page || selectedItemCount === 0 || isSelectionTransforming || activeItem?.isBackground) {
    return null;
  }

  const trackActionBarClick = (action: ItemActionBarAction): void => {
    trackPosterBuilderGA4Events(GA4EventName.EDITOR_ACTION_BAR_CLICKED, {[GA4EventParamName.ACTION]: action});
  };

  const getGroupUngroupAction = (): ItemActionBarLeadingAction | null => {
    if (isMobileVariant) {
      return null;
    }
    if (fullySelectedGroupId) {
      return {
        action: ItemActionBarAction.UNGROUP,
        id: `${CUSTOM_ITEM_ACTION_BAR_ID}-ungroup`,
        icon: 'icon-ungroup',
        ariaLabel: window.i18next.t('pmwjs_ungroup'),
        isDisabled: false,
        isSelected: false,
        onClick: (): void => {
          page.items.groups.ungroupSelection();
        },
      };
    }
    if (page.items.groups.isCurrentSelectionGroupable()) {
      return {
        action: ItemActionBarAction.GROUP,
        id: `${CUSTOM_ITEM_ACTION_BAR_ID}-group`,
        icon: 'icon-group-item',
        ariaLabel: window.i18next.t('pmwjs_group'),
        isDisabled: false,
        isSelected: false,
        onClick: (): void => {
          page.items.groups.groupSelection();
        },
      };
    }
    return null;
  };

  const getLeadingActions = (): ItemActionBarLeadingAction[] => {
    if (selectedItemCount > 1) {
      const groupUngroupAction = getGroupUngroupAction();
      return groupUngroupAction ? [groupUngroupAction] : [];
    }
    if (activeItem?.isSlideshow()) {
      return [
        {
          action: ItemActionBarAction.PREVIOUS_SLIDE,
          id: `${CUSTOM_ITEM_ACTION_BAR_ID}-previous-slide`,
          icon: 'icon-slide-prev',
          ariaLabel: window.i18next.t('pmwjs_previous'),
          isDisabled: !activeItem.slides.hasPreviousSlide(),
          isSelected: false,
          onClick: (): void => {
            activeItem.selectPreviousSlide();
          },
        },
        {
          action: ItemActionBarAction.NEXT_SLIDE,
          id: `${CUSTOM_ITEM_ACTION_BAR_ID}-next-slide`,
          icon: 'icon-slide-next',
          ariaLabel: window.i18next.t('pmwjs_next'),
          isDisabled: !activeItem.slides.hasNextSlide(),
          isSelected: false,
          onClick: (): void => {
            activeItem.selectNextSlide();
          },
        },
      ];
    }
    if (activeItem && isReplaceableOnBar(activeItem)) {
      return [
        {
          action: ItemActionBarAction.REPLACE,
          id: CUSTOM_REPLACE_CONTROL_ID,
          icon: 'icon-replace',
          ariaLabel: window.i18next.t('pmwjs_replace'),
          isDisabled: false,
          isSelected: isReplaceMenuOpen,
          onClick: (anchorRect): void => {
            trackPosterBuilderGA4Events(GA4EventName.REPLACE_ELEMENT, {[GA4EventParamName.SOURCE]: GA4EventSources.CANVAS});
            activeItem.onReplaceClicked(anchorRect);
          },
        },
      ];
    }
    if (activeItem?.isTableItem() || activeItem?.isMenu()) {
      return [
        {
          action: ItemActionBarAction.EDIT,
          id: `${CUSTOM_ITEM_ACTION_BAR_ID}-edit`,
          icon: 'icon-pencil',
          ariaLabel: window.i18next.t('pmwjs_edit'),
          isDisabled: false,
          isSelected: false,
          onClick: (): void => {
            activeItem.editItem();
          },
        },
      ];
    }
    return [];
  };

  const onDuplicateClicked = (): void => {
    trackActionBarClick(ItemActionBarAction.DUPLICATE);
    page.activeSelection.onDuplicate();
  };

  const onDeleteClicked = (): void => {
    trackActionBarClick(ItemActionBarAction.DELETE);
    trackEditorItemDeleted(GA4EventSources.CANVAS, getGa4ItemTypeForTimelineSelection());
    page.poster.deleteSelection();
  };

  const closeMoreMenu = (): void => {
    dispatch(
      updatePopUpStateAndPosition({
        openPosterPopUpMenu: false,
        popUpPosition: undefined,
        target: undefined,
        eventCanvasOffset: undefined,
      })
    );
  };

  const onMoreClicked = (event: MouseEvent<HTMLElement>): void => {
    if (isMoreMenuOpen) {
      closeMoreMenu();
      return;
    }
    trackItemMenuOpened(ItemMenuOpenSource.ACTION_BAR);
    if (isMobileVariant) {
      openPosterEditorMoreOptionsModal();
      return;
    }
    const anchorRect = getMenuAnchorRect(event);
    dispatch(
      updatePopUpStateAndPosition({
        openPosterPopUpMenu: true,
        popUpPosition: {top: anchorRect.bottom, left: anchorRect.left},
        target: page.activeSelection.getActiveObject(),
        eventCanvasOffset: undefined,
        anchorRect,
        openSource: ItemMenuOpenSource.ACTION_BAR,
      })
    );
  };

  const getLeadingButtons = (): ReactElement[] => {
    return getLeadingActions().map((leadingAction) => {
      return (
        <IconWithTooltip
          key={leadingAction.id}
          id={leadingAction.id}
          icon={leadingAction.icon}
          size={IconSize.SIZE_ICON_16}
          type={IconType.GHOST}
          shape={IconShape.CIRCLE}
          isUsedAsButton
          isDisabled={leadingAction.isDisabled}
          isSelected={leadingAction.isSelected}
          ariaLabel={leadingAction.ariaLabel}
          tooltipText={leadingAction.ariaLabel}
          placement={TooltipPlacement.BOTTOM}
          onClick={(event: MouseEvent<HTMLElement>): void => {
            trackActionBarClick(leadingAction.action);
            leadingAction.onClick(getMenuAnchorRect(event));
          }}
        />
      );
    });
  };

  const getBarStyle = (): CSSProperties => {
    const anchor = getItemActionBarAnchor(props.positionProperties);
    return {left: `${anchor.centerX}px`, top: `${anchor.top}px`};
  };

  return (
    <div id={CUSTOM_ITEM_ACTION_BAR_ID} className={`${styles.actionBar} flex-row-align-center`} style={getBarStyle()}>
      {getLeadingButtons()}
      <IconWithTooltip
        id={`${CUSTOM_ITEM_ACTION_BAR_ID}-duplicate`}
        icon="icon-copy"
        size={IconSize.SIZE_ICON_16}
        type={IconType.GHOST}
        shape={IconShape.CIRCLE}
        isUsedAsButton
        ariaLabel={window.i18next.t('pmwjs_duplicate')}
        tooltipText={window.i18next.t('pmwjs_duplicate')}
        placement={TooltipPlacement.BOTTOM}
        onClick={onDuplicateClicked}
      />
      <IconWithTooltip
        id={`${CUSTOM_ITEM_ACTION_BAR_ID}-delete`}
        icon="icon-delete"
        size={IconSize.SIZE_ICON_16}
        type={IconType.GHOST}
        shape={IconShape.CIRCLE}
        className={styles.deleteIcon}
        isUsedAsButton
        ariaLabel={window.i18next.t('pmwjs_delete')}
        tooltipText={window.i18next.t('pmwjs_delete')}
        placement={TooltipPlacement.BOTTOM}
        onClick={onDeleteClicked}
      />
      <span className="flex-center" {...{[POPUP_TOGGLE_MARKER_ATTR]: ''}}>
        <IconWithTooltip
          id={`${CUSTOM_ITEM_ACTION_BAR_ID}-more`}
          icon="icon-more-h"
          size={IconSize.SIZE_ICON_16}
          type={IconType.GHOST}
          shape={IconShape.CIRCLE}
          isSelected={isMoreMenuOpen}
          isUsedAsButton
          ariaLabel={window.i18next.t('pmwjs_more_options')}
          tooltipText={window.i18next.t('pmwjs_more_options')}
          placement={TooltipPlacement.BOTTOM}
          onClick={onMoreClicked}
        />
      </span>
    </div>
  );
}

/** A menu opened from a bar button drops from that button and clears the whole bar, so it never covers the other buttons. */
const getMenuAnchorRect = (event: MouseEvent<HTMLElement>): PopupAnchorRect => {
  const buttonRect = event.currentTarget.getBoundingClientRect();
  const barRect = (event.currentTarget.closest(`#${CUSTOM_ITEM_ACTION_BAR_ID}`) ?? event.currentTarget).getBoundingClientRect();
  return {top: barRect.top, bottom: barRect.bottom, left: buttonRect.left};
};

/** Photos get Replace on the bar. Icons and stickers are images too, but swapping one is rarely what the user is after, so they keep Replace in the side panel and menu only. */
const isReplaceableOnBar = (item: Item): item is ImageItem => {
  return item.isImage() && !isIconImageItem(item) && !isItemStickerImage(item);
};
